import React, { useState, useEffect } from 'react';
import { handleFileUpload as processFileUpload } from './excel_parser';
import { handleMasterDataFileUpload } from './master_data_parser';
import * as XLSX from 'xlsx';
import { formatToDDMonYYYY } from './dateUtils'; // Import the date formatting utility
import './MasterAssetData.css';

const MasterAssetData = ({ currentUser }) => {
  const canModify = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const [assets, setAssets] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [fileType, setFileType] = useState('masterdata'); // 'standard' or 'masterdata'
  const [editingAsset, setEditingAsset] = useState(null); // Asset being edited
  const [selectedAssets, setSelectedAssets] = useState([]); // For selection operations
  
  // Status filter - 'active', 'all', 'condemned', 'shifted', 'biomedical'
  const [statusFilter, setStatusFilter] = useState('active');
  
  // Status change modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeAsset, setStatusChangeAsset] = useState(null);
  const [newStatus, setNewStatus] = useState(''); // 'condemned', 'shifted', or 'biomedical'
  const [statusRemark, setStatusRemark] = useState('');
  const [statusDate, setStatusDate] = useState('');
  const [statusDocument, setStatusDocument] = useState(null); // PDF document attachment
  
  // Action menu dropdown state
  const [openActionMenu, setOpenActionMenu] = useState(null); // Stores asset ID of open menu
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(200); // Default to 200 items per page
  const [totalPages, setTotalPages] = useState(1);

  // Load assets from API on component mount
  useEffect(() => {
    loadAssets();

    const pollInterval = setInterval(() => {
      loadAssets();
    }, 15000);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  // Reset action menu when assets change (e.g., new equipment added)
  useEffect(() => {
    setOpenActionMenu(null);
  }, [assets]);

  // Filter assets when searchTerm or statusFilter changes
  useEffect(() => {
    console.log('🔍 Search term changed:', searchTerm);
    console.log('📚 All assets:', assets);
    console.log('👁️ Status filter:', statusFilter);
    
    let filtered = assets;
    
    // Filter by status
    if (statusFilter !== 'all') {
      const statusFiltered = filtered.filter(asset => {
        const status = asset.status || 'active';
        if (statusFilter === 'active') {
          return status === 'active';
        }
        return status === statusFilter;
      });
      // If no data found for selected status, show all assets
      filtered = statusFiltered.length > 0 ? statusFiltered : assets;
    }
    
    // Filter by search term
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      console.log('🔍 Filtering assets with term:', term);
      
      filtered = filtered.filter(asset => {
        // Check if asset exists
        if (!asset) {
          console.log('⚠️ Null asset found');
          return false;
        }
        
        // Create a function to safely get string values
        const getSearchableValue = (value) => {
          if (value === null || value === undefined) return '';
          if (typeof value === 'string') return value.toLowerCase();
          if (typeof value === 'number') return value.toString().toLowerCase();
          if (value instanceof Date) return value.toString().toLowerCase();
          return String(value).toLowerCase();
        };
        
        // Check each field for the search term
        const searchableFields = [
          getSearchableValue(asset.assetId),
          getSearchableValue(asset.serialNo),
          getSearchableValue(asset.floor),
          getSearchableValue(asset.department),
          getSearchableValue(asset.equipmentName),
          getSearchableValue(asset.manufacturer),
          getSearchableValue(asset.model),
          getSearchableValue(asset.installationDate),
          getSearchableValue(asset.pmDue),
          getSearchableValue(asset.pmDone),
          getSearchableValue(asset.calDue),
          getSearchableValue(asset.calDone),
          getSearchableValue(asset.type),
          getSearchableValue(asset.remarks),
          getSearchableValue(asset.status)
        ];
        
        // Check if any field contains the search term
        const matches = searchableFields.some(fieldValue => {
          const result = fieldValue.includes(term);
          return result;
        });
          
        console.log(`🔍 Asset ${asset.id || 'unknown'} matches: ${matches}`);
        return matches;
      });
    }
    
    setFilteredAssets(filtered);
    console.log('🎯 Filtered assets count:', filtered.length);
  }, [searchTerm, assets, statusFilter]);

  // Calculate pagination when filteredAssets or itemsPerPage changes
  useEffect(() => {
    const total = Math.ceil(filteredAssets.length / itemsPerPage);
    setTotalPages(total);
    // Reset to first page if current page is out of range
    if (currentPage > total && total > 0) {
      setCurrentPage(1);
    }
  }, [filteredAssets, itemsPerPage]);

  const loadAssets = async () => {
    try {
      const response = await fetch('/api/assets');
      if (!response.ok) {
        throw new Error(`Failed to load assets (${response.status})`);
      }

      const payload = await response.json();
      const parsedAssets = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : []);

      setAssets(parsedAssets);
      setFilteredAssets(parsedAssets);
    } catch (error) {
      console.error('❌ Failed to load assets:', error);
      showMessage('Failed to load assets from server', 'error');
      setAssets([]);
      setFilteredAssets([]);
    }
  };

  const persistAssets = async (updatedAssets) => {
    const response = await fetch('/api/assets/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assets: updatedAssets })
    });

    if (!response.ok) {
      throw new Error(`Failed to save assets (${response.status})`);
    }

    const payload = await response.json();
    const serverAssets = Array.isArray(payload?.data)
      ? payload.data
      : (Array.isArray(updatedAssets) ? updatedAssets : []);

    setAssets(serverAssets);
    setFilteredAssets(serverAssets);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAssets = filteredAssets.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Change items per page
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Start editing an asset
  const startEditing = (asset) => {
    // When starting to edit, format the dates for display in the input fields
    const formattedAsset = { ...asset };
    
    // Format date fields for editing
    formattedAsset.installationDate = formatToDDMonYYYY(asset.installationDate || '');
    formattedAsset.pmDue = formatToDDMonYYYY(asset.pmDue || '');
    formattedAsset.pmDone = formatToDDMonYYYY(asset.pmDone || '');
    formattedAsset.calDue = formatToDDMonYYYY(asset.calDue || '');
    formattedAsset.calDone = formatToDDMonYYYY(asset.calDone || '');
    
    setEditingAsset(formattedAsset);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingAsset(null);
  };

  // Save edited asset
  const saveEditing = async () => {
    try {
      // Create a copy of the editing asset
      const assetToSave = { ...editingAsset };
      
      // Convert formatted dates back to standard format for storage
      // This is a simple approach - in a real app, you might want more sophisticated date parsing
      const convertFormattedDate = (formattedDate) => {
        if (!formattedDate) return '';
        
        // If it's already in a standard format, keep it
        if (/^\d{4}-\d{2}-\d{2}/.test(formattedDate)) {
          return formattedDate;
        }
        
        // If it's in our DDMonYYYY format, convert it back to YYYY-MM-DD
        if (/^\d{2}-[A-Za-z]{3}-\d{4}/.test(formattedDate)) {
          const parts = formattedDate.split('-');
          if (parts.length === 3) {
            const day = parts[0];
            const monthAbbrev = parts[1];
            const year = parts[2];
            
            // Convert month abbreviation to number
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthIndex = monthNames.indexOf(monthAbbrev);
            
            if (monthIndex !== -1) {
              const month = String(monthIndex + 1).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          }
        }
        
        // For other formats, try to parse as date
        try {
          const date = new Date(formattedDate);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch (e) {
          // If parsing fails, return as is
        }
        
        return formattedDate;
      };
      
      // Convert dates back to standard format
      assetToSave.installationDate = convertFormattedDate(editingAsset.installationDate);
      assetToSave.pmDue = convertFormattedDate(editingAsset.pmDue);
      assetToSave.pmDone = convertFormattedDate(editingAsset.pmDone);
      assetToSave.calDue = convertFormattedDate(editingAsset.calDue);
      assetToSave.calDone = convertFormattedDate(editingAsset.calDone);
      
      const updatedAssets = assets.map(asset => 
        asset.id === editingAsset.id ? assetToSave : asset
      );
      
      await persistAssets(updatedAssets);
      setEditingAsset(null);
      showMessage('Asset updated successfully!', 'success');
    } catch (error) {
      console.error('Failed to update asset:', error);
      showMessage('Failed to update asset on server', 'error');
    }
  };

  // Handle input change for editing
  const handleEditChange = (field, value) => {
    setEditingAsset(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle asset selection for operations
  const toggleAssetSelection = (id) => {
    setSelectedAssets(prev => 
      prev.includes(id) 
        ? prev.filter(assetId => assetId !== id) 
        : [...prev, id]
    );
  };

  // Select all filtered assets
  const selectAllFiltered = () => {
    if (selectedAssets.length === filteredAssets.length && filteredAssets.length > 0) {
      // Deselect all
      setSelectedAssets([]);
    } else {
      // Select all filtered assets
      setSelectedAssets(filteredAssets.map(asset => asset.id));
    }
  };

  // Status change functions
  const openStatusModal = (asset, status) => {
    setStatusChangeAsset(asset);
    setNewStatus(status);
    setStatusRemark('');
    setStatusDate(new Date().toISOString().split('T')[0]); // Today's date in YYYY-MM-DD format
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setStatusChangeAsset(null);
    setNewStatus('');
    setStatusRemark('');
    setStatusDate('');
    setStatusDocument(null);
  };

  const confirmStatusChange = async () => {
    // Validate remark
    if (!statusRemark || statusRemark.trim().length < 5) {
      showMessage('Please enter a remark with at least 5 characters', 'error');
      return;
    }

    try {
      const updatedAssets = assets.map(asset => {
        if (asset.id === statusChangeAsset.id) {
          return {
            ...asset,
            status: newStatus,
            statusDate: statusDate,
            statusRemark: statusRemark.trim(),
            statusDocument: statusDocument, // Store PDF document
            remarks: `${asset.remarks || ''}\n[${newStatus.toUpperCase()} - ${formatToDDMonYYYY(statusDate)}]: ${statusRemark.trim()}`.trim()
          };
        }
        return asset;
      });

      await persistAssets(updatedAssets);
      
      const statusLabel = newStatus === 'condemned' ? 'Condemned' : newStatus === 'shifted' ? 'Shifted' : 'Biomedical';
      showMessage(`Asset marked as ${statusLabel} successfully!`, 'success');
      closeStatusModal();
    } catch (error) {
      console.error('Failed to update asset status:', error);
      showMessage('Failed to update asset status on server', 'error');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'condemned':
        return <span className="status-badge condemned">CONDEMNED</span>;
      case 'shifted':
        return <span className="status-badge shifted">SHIFTED</span>;
      case 'biomedical':
        return <span className="status-badge biomedical">BIOMEDICAL</span>;
      default:
        return <span className="status-badge active">ACTIVE</span>;
    }
  };

  const toggleActionMenu = (assetId) => {
    setOpenActionMenu(openActionMenu === assetId ? null : assetId);
  };

  const handleActionClick = (asset, status) => {
    setOpenActionMenu(null);
    openStatusModal(asset, status);
  };

  // Calculate active assets (excluding condemned and shifted)
  const activeAssetsCount = assets.filter(asset => {
    if (!asset || typeof asset !== 'object') return false;
    const status = asset.status || 'active';
    return status !== 'condemned' && status !== 'shifted';
  }).length;

  return (
    <div className="master-asset-data-container">
      <div className="card">
        <h2 className="card-title">📋 Master Asset Data</h2>
        
        {currentUser && (
          <div className="user-badge">
            👤 Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        
        <div className="master-data-info">
          <h3>Current Master Database Information</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <h4>Total Assets</h4>
              <div className="stat-value">{activeAssetsCount}</div>
            </div>
          </div>
          
          {/* Search functionality */}
          <div className="search-section">
            <div className="form-group">
              <label htmlFor="search">🔍 Search Assets:</label>
              <input
                type="text"
                id="search"
                placeholder="Search by Asset ID, Serial No, Equipment Name, Department, etc."
                value={searchTerm}
                onChange={(e) => {
                  console.log('⌨️ Search input changed:', e.target.value);
                  setSearchTerm(e.target.value);
                }}
              />
            </div>
          </div>
          
          {/* Assets table */}
          {filteredAssets.length > 0 && (
            <div className="assets-table-section">
              <h3>Database Assets ({filteredAssets.length} items)</h3>
              
              {/* Pagination controls */}
              <div className="pagination-controls">
                <div className="items-per-page">
                  <label>Show: </label>
                  <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                  <span> entries per page</span>
                </div>
                <div className="pagination-info">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAssets.length)} of {filteredAssets.length} entries
                </div>
              </div>
              
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>S.N</th>
                      <th>Department</th>
                      <th>Floor</th>
                      <th>Equipment</th>
                      <th>Asset ID</th>
                      <th>Manufacturer</th>
                      <th>Model</th>
                      <th>Serial No</th>
                      <th>Installation Date</th>
                      <th>PM DUE</th>
                      <th>PM DONE</th>
                      <th>CAL DUE</th>
                      <th>CAL DONE</th>
                      <th>Type</th>
                      <th>
                                              Status
                                              <select
                                                className="th-status-filter"
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                title="Filter by status"
                                              >
                                                <option value="active">👁️ Active</option>
                                                <option value="all">🙈 All</option>
                                                <option value="condemned">🗑️ Condemned</option>
                                                <option value="shifted">📦 Shifted</option>
                                                <option value="biomedical">🔬 Biomedical</option>
                                              </select>
                                            </th>
                      <th>Remarks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAssets.map((asset, index) => {
                      try {
                        return editingAsset && editingAsset.id === asset.id ? (
                          // Edit row
                          <tr key={asset.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.department || ''} 
                                onChange={(e) => handleEditChange('department', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.floor || ''} 
                                onChange={(e) => handleEditChange('floor', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.equipmentName || ''} 
                                onChange={(e) => handleEditChange('equipmentName', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.assetId || ''} 
                                onChange={(e) => handleEditChange('assetId', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.manufacturer || ''} 
                                onChange={(e) => handleEditChange('manufacturer', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.model || ''} 
                                onChange={(e) => handleEditChange('model', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.serialNo || ''} 
                                onChange={(e) => handleEditChange('serialNo', e.target.value)} 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.installationDate || ''} 
                                onChange={(e) => handleEditChange('installationDate', e.target.value)} 
                                placeholder="DD-Mon-YYYY (e.g., 15-Jan-2024)"
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.pmDue || ''} 
                                onChange={(e) => handleEditChange('pmDue', e.target.value)} 
                                placeholder="DD-Mon-YYYY (e.g., 15-Jan-2024)"
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.pmDone || ''} 
                                onChange={(e) => handleEditChange('pmDone', e.target.value)} 
                                placeholder="DD-Mon-YYYY (e.g., 15-Jan-2024)"
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.calDue || ''} 
                                onChange={(e) => handleEditChange('calDue', e.target.value)} 
                                placeholder="DD-Mon-YYYY (e.g., 15-Jan-2024)"
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.calDone || ''} 
                                onChange={(e) => handleEditChange('calDone', e.target.value)} 
                                placeholder="DD-Mon-YYYY (e.g., 15-Jan-2024)"
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                value={editingAsset.type || ''} 
                                onChange={(e) => handleEditChange('type', e.target.value)} 
                              />
                            </td>
                            <td>
                              {getStatusBadge(editingAsset.status || 'active')}
                            </td>
                            <td>
                              <textarea 
                                value={editingAsset.remarks || ''} 
                                onChange={(e) => handleEditChange('remarks', e.target.value)}
                                placeholder="Enter remarks (e.g., shifted to ICU, transferred from ward 5)"
                                rows="2"
                                style={{width: '100%', minWidth: '200px'}}
                              />
                            </td>
                            <td>
                              <button className="btn btn-success" onClick={saveEditing}>💾 Save</button>
                              <button className="btn btn-secondary" onClick={cancelEditing}>❌ Cancel</button>
                            </td>
                          </tr>
                        ) : (
                          // Display row
                          <tr key={asset.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{asset.department}</td>
                            <td>{asset.floor}</td>
                            <td>{asset.equipmentName}</td>
                            <td>{asset.assetId}</td>
                            <td>{asset.manufacturer}</td>
                            <td>{asset.model}</td>
                            <td>{asset.serialNo}</td>
                            <td>{formatToDDMonYYYY(asset.installationDate || '')}</td>
                            <td>{formatToDDMonYYYY(asset.pmDue || '')}</td>
                            <td>{formatToDDMonYYYY(asset.pmDone || '')}</td>
                            <td>{formatToDDMonYYYY(asset.calDue || '')}</td>
                            <td>{formatToDDMonYYYY(asset.calDone || '')}</td>
                            <td>{asset.type}</td>
                            <td>
                              {getStatusBadge(asset.status || 'active')}
                              {asset.statusDocument && asset.statusDocument.data && (
                                <button 
                                  className="btn-view-document"
                                  onClick={() => window.open(asset.statusDocument.data, '_blank')}
                                  title={`View attached document: ${asset.statusDocument.name}`}
                                >
                                  📄 View PDF
                                </button>
                              )}
                            </td>
                            <td style={{maxWidth: '200px', whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>{asset.remarks || ''}</td>
                            <td>
                              <div className="action-buttons">
                                <button type="button" className="btn btn-primary" onClick={() => startEditing(asset)} disabled={!canModify}>✏️ Edit</button>
                                <div className="action-menu-container">
                                  <button 
                                    type="button"
                                    className="btn btn-action-menu" 
                                    onClick={() => toggleActionMenu(asset.id)}
                                    title="More Actions"
                                    disabled={!canModify}
                                  >
                                    ⚙️ Actions ▼
                                  </button>
                                  {openActionMenu === asset.id && (
                                    <div className={`action-dropdown ${index >= currentAssets.length - 3 ? 'drop-up' : ''}`}>
                                      {(asset.status === 'condemned' || asset.status === 'shifted' || asset.status === 'biomedical') ? (
                                        // Show restore option for inactive assets
                                        <button 
                                          type="button"
                                          className="dropdown-item restore" 
                                          onClick={() => handleActionClick(asset, 'active')}
                                        >
                                          ✅ Restore to Active
                                        </button>
                                      ) : (
                                        // Show mark options for active assets
                                        <>
                                          <button 
                                            type="button"
                                            className="dropdown-item condemned" 
                                            onClick={() => handleActionClick(asset, 'condemned')}
                                          >
                                            🗑️ Mark as Condemned
                                          </button>
                                          <button 
                                            type="button"
                                            className="dropdown-item shifted" 
                                            onClick={() => handleActionClick(asset, 'shifted')}
                                          >
                                            📦 Mark as Shifted
                                          </button>
                                          <button 
                                            type="button"
                                            className="dropdown-item biomedical" 
                                            onClick={() => handleActionClick(asset, 'biomedical')}
                                          >
                                            🔬 Mark as Biomedical
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      } catch (error) {
                        console.error('Error rendering asset row:', asset, error);
                        // Render a fallback row if there's an error
                        return (
                          <tr key={asset.id || index}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td colSpan="16">Error rendering asset data</td>
                          </tr>
                        );
                      }
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first, last, current, and nearby pages
                    if (pageNumber === 1 || pageNumber === totalPages || 
                        (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)) {
                      return (
                        <button
                          key={pageNumber}
                          className={`btn ${currentPage === pageNumber ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => paginate(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                      // Show ellipsis for skipped pages
                      return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                    }
                    return null;
                  })}
                  
                  <button 
                    className="btn btn-secondary"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
          
          {filteredAssets.length === 0 && searchTerm.trim() !== '' && (
            <div className="no-results">
              <p>🔍 No assets found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Status Change Modal */}
      {showStatusModal && statusChangeAsset && (
        <div className="modal-overlay" onClick={closeStatusModal}>
          <div className="modal-content status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {newStatus === 'condemned' ? '🗑️ Mark as Condemned' : 
                 newStatus === 'shifted' ? '📦 Mark as Shifted' : 
                 newStatus === 'biomedical' ? '🔬 Mark as Biomedical' :
                 '✅ Restore to Active'}
              </h3>
              <button className="modal-close" onClick={closeStatusModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="asset-info">
                <p><strong>Equipment:</strong> {statusChangeAsset.equipmentName}</p>
                <p><strong>Asset ID:</strong> {statusChangeAsset.assetId}</p>
                <p><strong>Department:</strong> {statusChangeAsset.department}</p>
              </div>
              
              <div className="form-group">
                <label htmlFor="statusDate">Date:</label>
                <input
                  type="date"
                  id="statusDate"
                  value={statusDate}
                  onChange={(e) => setStatusDate(e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="statusRemark">
                  Remark: <span className="required">*</span>
                </label>
                <textarea
                  id="statusRemark"
                  value={statusRemark}
                  onChange={(e) => setStatusRemark(e.target.value)}
                  placeholder={`Enter reason for marking as ${newStatus} (minimum 5 characters)`}
                  rows="3"
                />
                {statusRemark && statusRemark.trim().length < 5 && (
                  <span className="validation-error">Remark must be at least 5 characters</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="statusDocument">
                  Attach Document (PDF):
                </label>
                <input
                  type="file"
                  id="statusDocument"
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && file.type === 'application/pdf') {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setStatusDocument({
                          name: file.name,
                          data: event.target.result,
                          type: file.type,
                          size: file.size
                        });
                      };
                      reader.readAsDataURL(file);
                    } else if (file) {
                      showMessage('Please select a valid PDF file', 'error');
                      e.target.value = '';
                    }
                  }}
                />
                {statusDocument && (
                  <div className="file-info">
                    <span className="file-name">📄 {statusDocument.name}</span>
                    <button 
                      className="btn-remove-file"
                      onClick={() => {
                        setStatusDocument(null);
                        document.getElementById('statusDocument').value = '';
                      }}
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeStatusModal}>
                Cancel
              </button>
              <button 
                className={`btn ${newStatus === 'condemned' ? 'btn-condemned' : newStatus === 'shifted' ? 'btn-shifted' : newStatus === 'biomedical' ? 'btn-biomedical' : 'btn-restore'}`}
                onClick={confirmStatusChange}
                disabled={!statusRemark || statusRemark.trim().length < 5}
              >
                Confirm {newStatus === 'condemned' ? 'Condemn' : newStatus === 'shifted' ? 'Shift' : newStatus === 'biomedical' ? 'Biomedical' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterAssetData;